import React from 'react';
import {
  Row, Col, Typography, Button, Badge, Avatar,
} from 'antd';
import {
  DashboardOutlined, TeamOutlined, DollarOutlined, ToolOutlined,
  SafetyCertificateOutlined, HomeOutlined, CarOutlined,
  BarChartOutlined, CheckCircleOutlined, ClockCircleOutlined,
  WarningOutlined, BankOutlined, UserOutlined,
  FileTextOutlined, ProjectOutlined,
  FundOutlined, SoundOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface VideoSlide {
  heading: string;
  visual: React.ReactNode;
}

export interface VideoDemo {
  id: string;
  title: string;
  subtitle: string;
  duration: string;
  category: string;
  roles: string[];
  icon: React.ReactNode;
  accentColor: string;
  slides: VideoSlide[];
}

// ─── Shared slide helpers ─────────────────────────────────────────────────────

const SlideShell: React.FC<{ children: React.ReactNode; bg?: string }> = ({
  children, bg = '#f8fafc',
}) => (
  <div style={{
    height: '100%', background: bg, display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: 20, boxSizing: 'border-box',
  }}>
    {children}
  </div>
);

const StatBox: React.FC<{ label: string; value: string | number; color: string; bg: string }> = ({
  label, value, color, bg,
}) => (
  <div style={{
    background: bg, border: `1px solid ${color}30`, borderRadius: 8,
    padding: '10px 14px', textAlign: 'center', minWidth: 90,
  }}>
    <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>{label}</div>
  </div>
);

const FakeTag: React.FC<{ color: string; bg: string; label: string }> = ({ color, bg, label }) => (
  <span style={{
    background: bg, color, border: `1px solid ${color}40`, borderRadius: 4,
    padding: '1px 8px', fontSize: 11, fontWeight: 600,
  }}>{label}</span>
);

const FakeRow: React.FC<{ cells: React.ReactNode[]; header?: boolean }> = ({ cells, header }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: `repeat(${cells.length}, 1fr)`,
    gap: 0, background: header ? '#fafafa' : '#fff',
    borderBottom: '1px solid #f0f0f0', padding: '6px 12px',
  }}>
    {cells.map((c, i) => (
      <div key={i} style={{ fontSize: header ? 11 : 12, fontWeight: header ? 700 : 400, color: header ? '#8c8c8c' : '#262626' }}>{c}</div>
    ))}
  </div>
);

const FakeModal: React.FC<{
  title: string; children: React.ReactNode; btnLabel?: string; color?: string;
}> = ({ title, children, btnLabel = 'Save', color = '#1677ff' }) => (
  <div style={{
    background: '#fff', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    width: 340, overflow: 'hidden',
  }}>
    <div style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0', padding: '10px 16px' }}>
      <Text strong style={{ fontSize: 14 }}>{title}</Text>
    </div>
    <div style={{ padding: 14 }}>{children}</div>
    <div style={{ padding: '8px 16px 12px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <Button size="small">Cancel</Button>
      <Button size="small" type="primary" style={{ background: color, borderColor: color }}>{btnLabel}</Button>
    </div>
  </div>
);

const FakeFormRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>{label}</div>
    <div style={{
      background: '#f8fafc', border: '1px solid #d9d9d9', borderRadius: 4,
      padding: '4px 8px', fontSize: 12, color: '#262626',
    }}>{value}</div>
  </div>
);

const HeroSlide: React.FC<{
  icon: React.ReactNode; title: string; subtitle: string; color: string;
  bullets: string[];
}> = ({ icon, title, subtitle, color, bullets }) => (
  <SlideShell bg={`linear-gradient(135deg, ${color}12 0%, ${color}06 100%)`}>
    <div style={{ textAlign: 'center', maxWidth: 400 }}>
      <div style={{
        width: 60, height: 60, borderRadius: 16, background: `${color}20`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 14px', fontSize: 28, color,
      }}>
        {icon}
      </div>
      <Title level={4} style={{ margin: '0 0 6px', color: '#1a202c' }}>{title}</Title>
      <Paragraph type="secondary" style={{ margin: '0 0 16px', fontSize: 13 }}>{subtitle}</Paragraph>
      <div style={{ textAlign: 'left', display: 'inline-block' }}>
        {bullets.map((b, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
            <CheckCircleOutlined style={{ color, marginTop: 2, flexShrink: 0 }} />
            <Text style={{ fontSize: 12 }}>{b}</Text>
          </div>
        ))}
      </div>
    </div>
  </SlideShell>
);

// ─── Video Library ────────────────────────────────────────────────────────────

export const VIDEO_DEMOS: VideoDemo[] = [

  // ── 1. Platform Overview (all roles) ──────────────────────────────────────
  {
    id: 'v-01',
    title: 'Platform Overview',
    subtitle: 'Navigate the dashboard and understand your workspace',
    duration: '2:30',
    category: 'Getting Started',
    roles: ['super_admin_condo', 'admin', 'owner', 'board-member'],
    icon: <DashboardOutlined />,
    accentColor: '#6366f1',
    slides: [
      {
        heading: 'Welcome to CondoPro Management',
        visual: (
          <HeroSlide
            icon={<DashboardOutlined />}
            title="CondoPro Platform Overview"
            subtitle="Your complete condominium management workspace"
            color="#6366f1"
            bullets={[
              'Role-based access — see only what you need',
              'Real-time data across all modules',
              'Responsive sidebar navigation with group sections',
              'Breadcrumb trail always shows your location',
            ]}
          />
        ),
      },
      {
        heading: 'Sidebar Navigation',
        visual: (
          <SlideShell>
            <div style={{ display: 'flex', gap: 16, width: '100%', maxWidth: 480 }}>
              {/* Fake sidebar */}
              <div style={{
                width: 160, background: 'linear-gradient(180deg, #1a2640 0%, #1e2d42 100%)',
                borderRadius: 8, padding: '12px 0', flexShrink: 0,
              }}>
                {[
                  { group: 'OVERVIEW', items: [{ icon: <DashboardOutlined />, label: 'Dashboard', active: true, color: '#6366f1' }] },
                  { group: 'RESIDENTS', items: [{ icon: <TeamOutlined />, label: 'Residents', active: false, color: '#14b8a6' }, { icon: <WarningOutlined />, label: 'Violations', active: false, color: '#14b8a6' }] },
                  { group: 'FINANCES', items: [{ icon: <DollarOutlined />, label: 'Fees', active: false, color: '#22c55e' }, { icon: <FundOutlined />, label: 'Financial Mgmt', active: false, color: '#22c55e' }] },
                ].map(g => (
                  <div key={g.group}>
                    <div style={{ padding: '8px 12px 3px', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: 1 }}>{g.group}</div>
                    {g.items.map(it => (
                      <div key={it.label} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '0 8px', height: 34, margin: '1px 6px', borderRadius: 6,
                        background: it.active ? 'rgba(255,255,255,0.12)' : 'transparent',
                        borderLeft: it.active ? `3px solid ${it.color}` : '3px solid transparent',
                      }}>
                        <span style={{ color: it.active ? it.color : 'rgba(255,255,255,0.5)', fontSize: 13 }}>{it.icon}</span>
                        <span style={{ color: it.active ? '#fff' : 'rgba(255,255,255,0.65)', fontSize: 11, fontWeight: it.active ? 600 : 400 }}>{it.label}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {/* Fake content */}
              <div style={{ flex: 1 }}>
                <div style={{ background: '#f0f5ff', border: '1px solid #d6e4ff', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
                  <Text style={{ fontSize: 11, color: '#4f46e5' }}>Dashboard › Overview</Text>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { label: 'Total Residents', value: '48', color: '#6366f1', bg: '#eef2ff' },
                    { label: 'Collection Rate', value: '94%', color: '#22c55e', bg: '#f0fdf4' },
                    { label: 'Open Requests', value: '7', color: '#f97316', bg: '#fff7ed' },
                    { label: 'Net Income', value: '$14.2K', color: '#0ea5e9', bg: '#f0f9ff' },
                  ].map(s => (
                    <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}20`, borderRadius: 6, padding: '8px 10px' }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: '#8c8c8c' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Role-Based Access Control',
        visual: (
          <SlideShell>
            <div style={{ maxWidth: 460, width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                Each role sees a tailored experience:
              </Text>
              {[
                { role: 'Super Admin', color: '#ef4444', bg: '#fef2f2', features: 'All modules + multi-property management' },
                { role: 'Property Manager', color: '#3b82f6', bg: '#eff6ff', features: 'Residents, finances, operations, maintenance' },
                { role: 'Board Member', color: '#8b5cf6', bg: '#f5f3ff', features: 'Governance, financials, oversight reports' },
                { role: 'Owner / Resident', color: '#10b981', bg: '#ecfdf5', features: 'My unit, payments, requests, amenities' },
              ].map(r => (
                <div key={r.role} style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
                  background: r.bg, border: `1px solid ${r.color}20`, borderRadius: 6, padding: '8px 12px',
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                  <div>
                    <Text strong style={{ fontSize: 12, color: r.color }}>{r.role}</Text>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{r.features}</Text>
                  </div>
                </div>
              ))}
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Property Switcher & Condo Selector',
        visual: (
          <SlideShell bg="#1a2640">
            <div style={{ width: 380 }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, display: 'block', marginBottom: 8 }}>ACTIVE PROPERTY</Text>
              <div style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                <Text style={{ color: '#fff', fontWeight: 600, display: 'block' }}>Ocean View Towers</Text>
                <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>48 Units · 94% Collection</Text>
              </div>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, display: 'block', marginBottom: 8 }}>SWITCH PROPERTY</Text>
              {['Mountain Peak Residences', 'City Center Apartments'].map((name, i) => (
                <div key={name} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, marginBottom: 4,
                  background: i === 0 ? 'rgba(255,255,255,0.1)' : 'transparent',
                  cursor: 'pointer',
                }}>
                  <BankOutlined style={{ color: i === 0 ? '#6366f1' : 'rgba(255,255,255,0.4)' }} />
                  <Text style={{ color: i === 0 ? '#fff' : 'rgba(255,255,255,0.55)', fontSize: 12 }}>{name}</Text>
                </div>
              ))}
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Quick Actions & Notifications',
        visual: (
          <SlideShell>
            <div style={{ width: 380 }}>
              <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
                {[
                  { icon: <SoundOutlined />, label: 'Send Notice', color: '#f97316' },
                  { icon: <TeamOutlined />, label: 'Add Resident', color: '#10b981' },
                  { icon: <DollarOutlined />, label: 'Record Payment', color: '#6366f1' },
                  { icon: <ToolOutlined />, label: 'Work Order', color: '#f59e0b' },
                ].map(a => (
                  <Col span={12} key={a.label}>
                    <div style={{
                      border: `1.5px solid ${a.color}30`, background: `${a.color}08`,
                      borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <span style={{ color: a.color, fontSize: 16 }}>{a.icon}</span>
                      <Text style={{ color: a.color, fontWeight: 600, fontSize: 12 }}>{a.label}</Text>
                    </div>
                  </Col>
                ))}
              </Row>
              <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: '8px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <WarningOutlined style={{ color: '#f97316' }} />
                  <Text strong style={{ fontSize: 12 }}>3 Overdue Accounts</Text>
                </div>
                <Text type="secondary" style={{ fontSize: 11 }}>Unit 302, Unit 415, Unit 507 — action required</Text>
              </div>
            </div>
          </SlideShell>
        ),
      },
    ],
  },

  // ── 2. Resident Management (admin+) ─────────────────────────────────────
  {
    id: 'v-02',
    title: 'Resident Management',
    subtitle: 'Add owners, manage units, track profiles and occupancy',
    duration: '3:15',
    category: 'Property Administration',
    roles: ['super_admin_condo', 'admin'],
    icon: <TeamOutlined />,
    accentColor: '#14b8a6',
    slides: [
      {
        heading: 'Resident Management Overview',
        visual: (
          <HeroSlide
            icon={<TeamOutlined />}
            title="Resident Management"
            subtitle="Maintain complete owner records for every unit"
            color="#14b8a6"
            bullets={[
              'Owner profiles with contact info and unit assignment',
              'Occupancy and lease status tracking',
              'Link residents to fees, payments and maintenance history',
              'Filter by unit, status, or outstanding balance',
            ]}
          />
        ),
      },
      {
        heading: 'Owner List View',
        visual: (
          <SlideShell>
            <div style={{ width: '100%', maxWidth: 480 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <Text strong style={{ fontSize: 14 }}>Residents · 48 total</Text>
                <Button size="small" type="primary" style={{ background: '#14b8a6', borderColor: '#14b8a6', fontSize: 11 }}>+ Add Owner</Button>
              </div>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                <FakeRow header cells={['Unit', 'Owner Name', 'Email', 'Status']} />
                {[
                  { unit: '101', name: 'Sarah Chen', email: 'schen@mail.com', status: 'ACTIVE', sc: '#14b8a6', sb: '#ecfdf5' },
                  { unit: '202', name: 'James Park', email: 'jpark@mail.com', status: 'ACTIVE', sc: '#14b8a6', sb: '#ecfdf5' },
                  { unit: '305', name: 'Maria Silva', email: 'msilva@mail.com', status: 'TENANT', sc: '#f59e0b', sb: '#fffbeb' },
                  { unit: '411', name: 'Tom Wright', email: 'twright@mail.com', status: 'OVERDUE', sc: '#ef4444', sb: '#fef2f2' },
                ].map(r => (
                  <FakeRow key={r.unit} cells={[
                    <Text strong style={{ fontSize: 12 }}>{r.unit}</Text>,
                    <Text style={{ fontSize: 12 }}>{r.name}</Text>,
                    <Text type="secondary" style={{ fontSize: 11 }}>{r.email}</Text>,
                    <FakeTag color={r.sc} bg={r.sb} label={r.status} />,
                  ]} />
                ))}
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Adding a New Owner',
        visual: (
          <SlideShell bg="#f5f5f5">
            <FakeModal title="Add New Owner" btnLabel="Create Owner" color="#14b8a6">
              <FakeFormRow label="Full Name" value="Alexandra Thompson" />
              <FakeFormRow label="Email Address" value="athompson@email.com" />
              <FakeFormRow label="Phone Number" value="+1 (416) 555-0192" />
              <FakeFormRow label="Unit Number" value="Unit 318" />
              <FakeFormRow label="Move-In Date" value="2025-05-01" />
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Ownership Status</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['Owner-Occupied', 'Tenant', 'Investor'].map((o, i) => (
                    <div key={o} style={{
                      padding: '3px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
                      background: i === 0 ? '#14b8a6' : '#f5f5f5',
                      color: i === 0 ? '#fff' : '#595959',
                      border: `1px solid ${i === 0 ? '#14b8a6' : '#d9d9d9'}`,
                    }}>{o}</div>
                  ))}
                </div>
              </div>
            </FakeModal>
          </SlideShell>
        ),
      },
      {
        heading: 'Unit Portal — Owner Profile',
        visual: (
          <SlideShell>
            <div style={{ width: 420 }}>
              <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
                <Avatar size={48} style={{ background: '#14b8a6', flexShrink: 0 }}>SC</Avatar>
                <div>
                  <Text strong style={{ fontSize: 16, display: 'block' }}>Sarah Chen</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>Unit 101 · Owner-Occupied · Since Jan 2021</Text>
                  <div style={{ marginTop: 4 }}>
                    <FakeTag color="#14b8a6" bg="#ecfdf5" label="ACTIVE" />
                  </div>
                </div>
              </div>
              <Row gutter={8} style={{ marginBottom: 12 }}>
                <Col span={8}><StatBox label="Balance" value="$0" color="#10b981" bg="#f0fdf4" /></Col>
                <Col span={8}><StatBox label="Open Requests" value="1" color="#f97316" bg="#fff7ed" /></Col>
                <Col span={8}><StatBox label="Payments" value="24" color="#6366f1" bg="#eef2ff" /></Col>
              </Row>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px' }}>
                <Text type="secondary" style={{ fontSize: 11 }}>📧 schen@mail.com &nbsp;·&nbsp; 📞 +1 (416) 555-0123</Text>
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Violation & Compliance Tracking',
        visual: (
          <SlideShell>
            <div style={{ width: '100%', maxWidth: 460 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <Text strong style={{ fontSize: 14 }}>Active Violations · 4 open</Text>
                <Button size="small" style={{ fontSize: 11 }}>+ Log Violation</Button>
              </div>
              {[
                { unit: '302', type: 'Noise', status: 'open', sc: '#ef4444', sb: '#fef2f2', date: 'Apr 12' },
                { unit: '411', type: 'Parking', status: 'notice_sent', sc: '#f59e0b', sb: '#fffbeb', date: 'Apr 8' },
                { unit: '507', type: 'Pet Policy', status: 'escalated', sc: '#f97316', sb: '#fff7ed', date: 'Mar 29' },
              ].map(v => (
                <div key={v.unit} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  border: '1px solid #f0f0f0', borderRadius: 6, marginBottom: 6, background: '#fff',
                }}>
                  <WarningOutlined style={{ color: v.sc }} />
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ fontSize: 12 }}>Unit {v.unit} — {v.type}</Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>Reported {v.date}</Text>
                  </div>
                  <FakeTag color={v.sc} bg={v.sb} label={v.status.replace('_', ' ')} />
                </div>
              ))}
            </div>
          </SlideShell>
        ),
      },
    ],
  },

  // ── 3. Fee & Payment Management (admin+) ─────────────────────────────────
  {
    id: 'v-03',
    title: 'Fee & Payment Management',
    subtitle: 'Create fees, record payments, and track collection rates',
    duration: '3:00',
    category: 'Financial Management',
    roles: ['super_admin_condo', 'admin'],
    icon: <DollarOutlined />,
    accentColor: '#22c55e',
    slides: [
      {
        heading: 'Fee & Payment Management Overview',
        visual: (
          <HeroSlide
            icon={<DollarOutlined />}
            title="Fee & Payment Management"
            subtitle="Keep your collection rates high and accounts current"
            color="#22c55e"
            bullets={[
              'Create maintenance fees, special assessments, fines',
              'Bulk-generate monthly fees for all units at once',
              'Record payments by cash, cheque, EFT, or portal',
              'Auto-apply late fees via the Late Fee Engine',
            ]}
          />
        ),
      },
      {
        heading: 'Fee Collection Dashboard',
        visual: (
          <SlideShell>
            <div style={{ width: 420 }}>
              <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
                <Col span={6}><StatBox label="Collected" value="$41.2K" color="#22c55e" bg="#f0fdf4" /></Col>
                <Col span={6}><StatBox label="Pending" value="$3.1K" color="#f59e0b" bg="#fffbeb" /></Col>
                <Col span={6}><StatBox label="Overdue" value="$1.8K" color="#ef4444" bg="#fef2f2" /></Col>
                <Col span={6}><StatBox label="Rate" value="94%" color="#6366f1" bg="#eef2ff" /></Col>
              </Row>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                <FakeRow header cells={['Unit', 'Description', 'Amount', 'Due', 'Status']} />
                {[
                  { unit: '101', desc: 'Monthly Fee', amt: '$350', due: 'May 1', status: 'PAID', sc: '#22c55e', sb: '#f0fdf4' },
                  { unit: '202', desc: 'Monthly Fee', amt: '$350', due: 'May 1', status: 'PENDING', sc: '#f59e0b', sb: '#fffbeb' },
                  { unit: '411', desc: 'Monthly Fee', amt: '$350', due: 'Apr 1', status: 'OVERDUE', sc: '#ef4444', sb: '#fef2f2' },
                ].map(r => (
                  <FakeRow key={r.unit} cells={[
                    r.unit, r.desc,
                    <Text strong style={{ fontSize: 12 }}>{r.amt}</Text>,
                    r.due,
                    <FakeTag color={r.sc} bg={r.sb} label={r.status} />,
                  ]} />
                ))}
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Creating a Fee',
        visual: (
          <SlideShell bg="#f5f5f5">
            <FakeModal title="Create New Fee" btnLabel="Create Fee" color="#22c55e">
              <FakeFormRow label="Fee Type" value="Monthly Maintenance Fee" />
              <FakeFormRow label="Amount" value="$350.00" />
              <FakeFormRow label="Due Date" value="2025-05-01" />
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Apply To</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {['All Units', 'Select Units', 'Specific Unit'].map((o, i) => (
                    <div key={o} style={{
                      padding: '3px 8px', borderRadius: 4, fontSize: 11,
                      background: i === 0 ? '#22c55e' : '#f5f5f5',
                      color: i === 0 ? '#fff' : '#595959',
                      border: `1px solid ${i === 0 ? '#22c55e' : '#d9d9d9'}`,
                    }}>{o}</div>
                  ))}
                </div>
              </div>
              <FakeFormRow label="Description / Notes" value="April 2025 monthly maintenance" />
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, padding: '6px 8px', fontSize: 11, color: '#166534' }}>
                ✓ This will create 48 fee records — one per unit
              </div>
            </FakeModal>
          </SlideShell>
        ),
      },
      {
        heading: 'Recording a Payment',
        visual: (
          <SlideShell bg="#f5f5f5">
            <FakeModal title="Record Payment — Unit 202" btnLabel="Record Payment" color="#22c55e">
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '6px 8px', marginBottom: 10, fontSize: 11 }}>
                Outstanding balance: <strong>$350.00</strong> — due May 1, 2025
              </div>
              <FakeFormRow label="Payment Amount" value="$350.00" />
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Payment Method</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {['EFT / Transfer', 'Cheque', 'Cash', 'Credit Card'].map((m, i) => (
                    <div key={m} style={{
                      padding: '3px 8px', borderRadius: 4, fontSize: 11,
                      background: i === 0 ? '#22c55e' : '#f5f5f5',
                      color: i === 0 ? '#fff' : '#595959',
                      border: `1px solid ${i === 0 ? '#22c55e' : '#d9d9d9'}`,
                    }}>{m}</div>
                  ))}
                </div>
              </div>
              <FakeFormRow label="Payment Date" value="2025-04-28" />
              <FakeFormRow label="Reference / Cheque No." value="EFT-20250428-4421" />
            </FakeModal>
          </SlideShell>
        ),
      },
      {
        heading: 'Late Fee Engine',
        visual: (
          <SlideShell>
            <div style={{ width: 420 }}>
              <div style={{ background: 'linear-gradient(135deg, #fff7ed, #fef3c7)', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <ClockCircleOutlined style={{ color: '#f97316', fontSize: 16 }} />
                  <Text strong style={{ fontSize: 13 }}>Late Fee Engine — Active Policy</Text>
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>Flat fee of $25.00 applied after 5-day grace period. Runs nightly at 01:00 AM.</Text>
              </div>
              <Row gutter={8} style={{ marginBottom: 12 }}>
                <Col span={8}><StatBox label="Would Apply" value="3" color="#6366f1" bg="#eef2ff" /></Col>
                <Col span={8}><StatBox label="Skipped" value="1" color="#f59e0b" bg="#fffbeb" /></Col>
                <Col span={8}><StatBox label="Total" value="$75" color="#22c55e" bg="#f0fdf4" /></Col>
              </Row>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="small" icon={<CheckCircleOutlined />}>Preview (Dry Run)</Button>
                <Button size="small" type="primary" danger>Run Now</Button>
              </div>
            </div>
          </SlideShell>
        ),
      },
    ],
  },

  // ── 4. Maintenance Operations (admin+) ────────────────────────────────────
  {
    id: 'v-04',
    title: 'Maintenance Operations',
    subtitle: 'Manage work orders, repair requests, inspections and vendors',
    duration: '3:30',
    category: 'Operations & Maintenance',
    roles: ['super_admin_condo', 'admin'],
    icon: <ToolOutlined />,
    accentColor: '#f97316',
    slides: [
      {
        heading: 'Maintenance Operations Overview',
        visual: (
          <HeroSlide
            icon={<ToolOutlined />}
            title="Maintenance Operations"
            subtitle="Keep buildings running and residents satisfied"
            color="#f97316"
            bullets={[
              'Resident maintenance requests with priority triage',
              'Internal work orders for planned and capital work',
              'Vendor assignment and progress tracking',
              'Inspection scheduling and corrective action tracking',
            ]}
          />
        ),
      },
      {
        heading: 'Maintenance Hub — Tabbed View',
        visual: (
          <SlideShell>
            <div style={{ width: '100%', maxWidth: 460 }}>
              {/* Tab bar */}
              <div style={{ display: 'flex', borderBottom: '2px solid #f0f0f0', marginBottom: 10, gap: 0 }}>
                {[
                  { label: 'Work Orders', active: true, badge: 7 },
                  { label: 'Maintenance Requests', active: false, badge: 4 },
                ].map(t => (
                  <div key={t.label} style={{
                    padding: '6px 14px', fontSize: 12, fontWeight: t.active ? 600 : 400, cursor: 'pointer',
                    borderBottom: t.active ? '2px solid #f97316' : 'none', marginBottom: t.active ? -2 : 0,
                    color: t.active ? '#f97316' : '#8c8c8c',
                  }}>
                    {t.label} <Badge count={t.badge} size="small" style={{ background: t.active ? '#f97316' : '#d9d9d9', marginLeft: 4 }} />
                  </div>
                ))}
              </div>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                <FakeRow header cells={['#', 'Title', 'Priority', 'Status']} />
                {[
                  { num: 'WO-042', title: 'Lobby HVAC Filter', pri: 'LOW', pc: '#6b7280', status: 'OPEN', sc: '#f59e0b', sb: '#fffbeb' },
                  { num: 'WO-041', title: 'Elevator B Maintenance', pri: 'HIGH', pc: '#f97316', status: 'IN PROGRESS', sc: '#f97316', sb: '#fff7ed' },
                  { num: 'WO-040', title: 'Roof Drain Repair', pri: 'URGENT', pc: '#ef4444', status: 'OPEN', sc: '#ef4444', sb: '#fef2f2' },
                ].map(r => (
                  <FakeRow key={r.num} cells={[
                    <Text type="secondary" style={{ fontSize: 11 }}>{r.num}</Text>,
                    <Text style={{ fontSize: 12 }}>{r.title}</Text>,
                    <FakeTag color={r.pc} bg={`${r.pc}15`} label={r.pri} />,
                    <FakeTag color={r.sc} bg={r.sb} label={r.status} />,
                  ]} />
                ))}
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Creating a Work Order',
        visual: (
          <SlideShell bg="#f5f5f5">
            <FakeModal title="Create Work Order" btnLabel="Create" color="#f97316">
              <FakeFormRow label="Title" value="Elevator B — Quarterly Service" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>Category</div>
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}>Inspection</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>Priority</div>
                  <div style={{ border: '1px solid #f97316', borderRadius: 4, padding: '4px 8px', fontSize: 12, color: '#f97316', background: '#fff7ed' }}>HIGH</div>
                </div>
              </div>
              <FakeFormRow label="Vendor / Contractor" value="Otis Elevator Services" />
              <FakeFormRow label="Scheduled Date" value="2025-05-12" />
              <FakeFormRow label="Estimated Cost" value="$850.00" />
            </FakeModal>
          </SlideShell>
        ),
      },
      {
        heading: 'Resident Maintenance Requests',
        visual: (
          <SlideShell>
            <div style={{ width: '100%', maxWidth: 460 }}>
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12 }}>
                <WarningOutlined style={{ color: '#f97316', marginRight: 6 }} />
                <strong>1 URGENT request</strong> — Unit 305: water flooding from bathroom
              </div>
              {[
                { req: 'MR-018', unit: '305', cat: 'Plumbing', title: 'Bathroom flooding', pri: 'URGENT', sc: '#ef4444', status: 'Open' },
                { req: 'MR-017', unit: '112', cat: 'HVAC', title: 'No heat in bedroom', pri: 'HIGH', sc: '#f97316', status: 'Assigned' },
                { req: 'MR-016', unit: '204', cat: 'Electrical', title: 'Flickering lights', pri: 'MEDIUM', sc: '#f59e0b', status: 'In Progress' },
              ].map(r => (
                <div key={r.req} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  border: `1px solid ${r.sc}20`, borderLeft: `3px solid ${r.sc}`, borderRadius: 6, marginBottom: 6, background: '#fff',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Text strong style={{ fontSize: 12 }}>Unit {r.unit}</Text>
                      <FakeTag color={r.sc} bg={`${r.sc}10`} label={r.pri} />
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>{r.cat} — {r.title}</Text>
                  </div>
                  <FakeTag color="#8c8c8c" bg="#f5f5f5" label={r.status} />
                </div>
              ))}
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Operations Hub — Projects & Inspections',
        visual: (
          <SlideShell>
            <div style={{ width: '100%', maxWidth: 440 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {[
                  { label: 'Projects', icon: <ProjectOutlined />, count: 3, color: '#722ed1' },
                  { label: 'Inspections', icon: <FileTextOutlined />, count: 2, color: '#f97316' },
                  { label: 'Vendors', icon: <TeamOutlined />, count: 12, color: '#1677ff' },
                ].map(t => (
                  <div key={t.label} style={{
                    flex: 1, border: `1px solid ${t.color}30`, borderRadius: 8,
                    padding: '10px 12px', background: `${t.color}06`, textAlign: 'center',
                  }}>
                    <div style={{ color: t.color, fontSize: 18, marginBottom: 4 }}>{t.icon}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: t.color }}>{t.count}</div>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>{t.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                <FakeRow header cells={['Project', 'Budget', 'Status']} />
                {[
                  { title: 'Lobby Renovation', budget: '$48,000', status: 'IN PROGRESS', sc: '#f97316', sb: '#fff7ed' },
                  { title: 'Roof Replacement', budget: '$120,000', status: 'PLANNING', sc: '#6366f1', sb: '#eef2ff' },
                ].map(p => (
                  <FakeRow key={p.title} cells={[
                    <Text style={{ fontSize: 12 }}>{p.title}</Text>,
                    <Text strong style={{ fontSize: 12 }}>{p.budget}</Text>,
                    <FakeTag color={p.sc} bg={p.sb} label={p.status} />,
                  ]} />
                ))}
              </div>
            </div>
          </SlideShell>
        ),
      },
    ],
  },

  // ── 5. Board Governance (board-member+) ───────────────────────────────────
  {
    id: 'v-05',
    title: 'Board Portal & Governance',
    subtitle: 'Meetings, resolutions, voting, and financial oversight',
    duration: '3:30',
    category: 'Governance & Board',
    roles: ['super_admin_condo', 'admin', 'board-member'],
    icon: <SafetyCertificateOutlined />,
    accentColor: '#8b5cf6',
    slides: [
      {
        heading: 'Board Portal Overview',
        visual: (
          <HeroSlide
            icon={<SafetyCertificateOutlined />}
            title="Board Portal & Governance"
            subtitle="Govern your corporation with transparency and accountability"
            color="#8b5cf6"
            bullets={[
              'Board member portal with KPI summary at a glance',
              'Schedule and minute board meetings',
              'Draft resolutions and record formal votes',
              'Financial oversight with reserve fund tracking',
            ]}
          />
        ),
      },
      {
        heading: 'Board Member KPI Dashboard',
        visual: (
          <SlideShell>
            <div style={{ width: 420 }}>
              <div style={{ background: 'linear-gradient(135deg, #1e293b, #334155)', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, display: 'block', marginBottom: 8 }}>BOARD OVERVIEW — Ocean View Towers</Text>
                <Row gutter={[8, 8]}>
                  {[
                    { label: 'Collection Rate', value: '94%', color: '#34d399' },
                    { label: 'Reserve Fund', value: '$214K', color: '#818cf8' },
                    { label: 'Open Violations', value: '4', color: '#fbbf24' },
                    { label: 'Open Work Orders', value: '7', color: '#f87171' },
                  ].map(s => (
                    <Col span={12} key={s.label}>
                      <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 6, padding: '8px 10px' }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>{s.label}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
              <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 6, padding: '8px 12px' }}>
                <Text strong style={{ fontSize: 12, color: '#7c3aed', display: 'block', marginBottom: 4 }}>Next Board Meeting</Text>
                <Text style={{ fontSize: 12 }}>📅 May 14, 2025 — 7:00 PM · Board Room A</Text>
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Scheduling & Managing Board Meetings',
        visual: (
          <SlideShell bg="#f5f5f5">
            <FakeModal title="Schedule Board Meeting" btnLabel="Schedule" color="#8b5cf6">
              <FakeFormRow label="Meeting Title" value="May 2025 Regular Board Meeting" />
              <FakeFormRow label="Date & Time" value="2025-05-14 · 7:00 PM" />
              <FakeFormRow label="Location" value="Board Room A — 123 Main St" />
              <FakeFormRow label="Agenda Items" value="Financial Review, Lobby Renovation, Q2 Budget" />
              <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 4, padding: '6px 8px', fontSize: 11, color: '#7c3aed' }}>
                📧 Invitation will be sent to all 5 board members
              </div>
            </FakeModal>
          </SlideShell>
        ),
      },
      {
        heading: 'Resolutions & Voting',
        visual: (
          <SlideShell>
            <div style={{ width: 420 }}>
              <div style={{ border: '1px solid #ddd6fe', borderRadius: 8, padding: '12px 14px', background: '#f5f3ff', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <Text strong style={{ fontSize: 13 }}>RES-2025-014</Text>
                  <FakeTag color="#8b5cf6" bg="#ede9fe" label="OPEN FOR VOTE" />
                </div>
                <Paragraph style={{ fontSize: 12, margin: '0 0 10px', color: '#374151' }}>
                  BE IT RESOLVED THAT the board authorizes the lobby renovation contract with ABC Contractors Inc. for $48,000, to be funded from the reserve fund.
                </Paragraph>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="small" style={{ background: '#22c55e', borderColor: '#22c55e', color: '#fff', fontSize: 11 }}>👍 Vote FOR</Button>
                  <Button size="small" danger style={{ fontSize: 11 }}>👎 Vote AGAINST</Button>
                  <Button size="small" style={{ fontSize: 11 }}>— ABSTAIN</Button>
                </div>
              </div>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 12px', background: '#fff' }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Current Vote Tally</Text>
                <div style={{ display: 'flex', gap: 8 }}>
                  <FakeTag color="#22c55e" bg="#f0fdf4" label="FOR: 3" />
                  <FakeTag color="#ef4444" bg="#fef2f2" label="AGAINST: 1" />
                  <FakeTag color="#8c8c8c" bg="#f5f5f5" label="ABSTAIN: 0" />
                  <FakeTag color="#f59e0b" bg="#fffbeb" label="PENDING: 1" />
                </div>
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Financial Oversight Reports',
        visual: (
          <SlideShell>
            <div style={{ width: 420 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>Financial Management — Board View</Text>
              <Row gutter={8} style={{ marginBottom: 12 }}>
                <Col span={8}><StatBox label="Operating Budget" value="$186K" color="#22c55e" bg="#f0fdf4" /></Col>
                <Col span={8}><StatBox label="YTD Expenses" value="$61K" color="#f97316" bg="#fff7ed" /></Col>
                <Col span={8}><StatBox label="Reserve Fund" value="$214K" color="#8b5cf6" bg="#f5f3ff" /></Col>
              </Row>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                <FakeRow header cells={['Category', 'Budget', 'Actual', 'Variance']} />
                {[
                  { cat: 'Insurance', budget: '$28,000', actual: '$27,200', var: '+$800', good: true },
                  { cat: 'Landscaping', budget: '$12,000', actual: '$9,400', var: '+$2,600', good: true },
                  { cat: 'Maintenance', budget: '$45,000', actual: '$48,200', var: '-$3,200', good: false },
                ].map(r => (
                  <FakeRow key={r.cat} cells={[
                    r.cat, r.budget, r.actual,
                    <Text style={{ fontSize: 12, color: r.good ? '#22c55e' : '#ef4444', fontWeight: 600 }}>{r.var}</Text>,
                  ]} />
                ))}
              </div>
            </div>
          </SlideShell>
        ),
      },
    ],
  },

  // ── 6. Resident Self-Service (owner) ─────────────────────────────────────
  {
    id: 'v-06',
    title: 'Resident Self-Service',
    subtitle: 'Pay fees, submit maintenance requests, and book amenities',
    duration: '2:30',
    category: 'Resident Self-Service',
    roles: ['owner'],
    icon: <HomeOutlined />,
    accentColor: '#10b981',
    slides: [
      {
        heading: 'My Home — Resident Portal',
        visual: (
          <HeroSlide
            icon={<HomeOutlined />}
            title="My Home Portal"
            subtitle="Everything you need to manage your unit in one place"
            color="#10b981"
            bullets={[
              'View your account balance and payment history',
              'Submit maintenance requests and track progress',
              'Authorize visitors and manage access',
              'Book shared amenities and view schedules',
            ]}
          />
        ),
      },
      {
        heading: 'My Unit Dashboard',
        visual: (
          <SlideShell>
            <div style={{ width: 380 }}>
              <div style={{ background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', border: '1px solid #a7f3d0', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <Text strong style={{ fontSize: 16, display: 'block' }}>Unit 202 · James Park</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>Ocean View Towers</Text>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>$0.00</div>
                    <FakeTag color="#10b981" bg="#d1fae5" label="CURRENT" />
                  </div>
                </div>
              </div>
              <Row gutter={8}>
                <Col span={8}><StatBox label="Next Fee" value="May 1" color="#6366f1" bg="#eef2ff" /></Col>
                <Col span={8}><StatBox label="Fee Amount" value="$350" color="#f97316" bg="#fff7ed" /></Col>
                <Col span={8}><StatBox label="Open Requests" value="1" color="#f59e0b" bg="#fffbeb" /></Col>
              </Row>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Submitting a Maintenance Request',
        visual: (
          <SlideShell bg="#f5f5f5">
            <FakeModal title="Submit Maintenance Request" btnLabel="Submit Request" color="#10b981">
              <FakeFormRow label="Issue Title" value="Bathroom faucet dripping — won't stop" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>Category</div>
                  <div style={{ border: '1px solid #10b981', borderRadius: 4, padding: '4px 8px', fontSize: 12, color: '#10b981', background: '#ecfdf5' }}>Plumbing</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>Priority</div>
                  <div style={{ border: '1px solid #f59e0b', borderRadius: 4, padding: '4px 8px', fontSize: 12, color: '#f59e0b', background: '#fffbeb' }}>Medium</div>
                </div>
              </div>
              <FakeFormRow label="Description" value="Master bathroom hot water faucet has been dripping continuously for 3 days. Wasteful and keeping me up at night." />
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, padding: '5px 8px', fontSize: 11, color: '#166534' }}>
                ✓ Request will be submitted for Unit 202
              </div>
            </FakeModal>
          </SlideShell>
        ),
      },
      {
        heading: 'Tracking Your Requests',
        visual: (
          <SlideShell>
            <div style={{ width: '100%', maxWidth: 400 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>My Maintenance Requests</Text>
              {[
                { num: 'MR-018', title: 'Dripping faucet', cat: 'Plumbing', status: 'Submitted', sc: '#6366f1', sb: '#eef2ff', date: 'Today' },
                { num: 'MR-012', title: 'Thermostat issue', cat: 'HVAC', status: 'In Progress', sc: '#f97316', sb: '#fff7ed', date: 'Apr 10' },
                { num: 'MR-007', title: 'Broken outlet', cat: 'Electrical', status: 'Completed', sc: '#22c55e', sb: '#f0fdf4', date: 'Mar 20' },
              ].map(r => (
                <div key={r.num} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                  border: '1px solid #f0f0f0', borderRadius: 6, marginBottom: 6, background: '#fff',
                }}>
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ fontSize: 12 }}>{r.title}</Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>{r.cat} · {r.date}</Text>
                  </div>
                  <FakeTag color={r.sc} bg={r.sb} label={r.status} />
                </div>
              ))}
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Booking an Amenity',
        visual: (
          <SlideShell>
            <div style={{ width: 380 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>Available Amenities</Text>
              {[
                { name: 'Fitness Centre', icon: '🏋️', avail: 'Open · 6 AM–10 PM', booked: false, color: '#10b981' },
                { name: 'Party Room', icon: '🎉', avail: '2 bookings this weekend', booked: false, color: '#8b5cf6' },
                { name: 'Rooftop Terrace', icon: '🌅', avail: 'Your booking: May 10, 2–5 PM', booked: true, color: '#f97316' },
              ].map(a => (
                <div key={a.name} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  border: `1px solid ${a.color}30`, borderRadius: 8, marginBottom: 8,
                  background: a.booked ? `${a.color}06` : '#fff',
                }}>
                  <div style={{ fontSize: 24 }}>{a.icon}</div>
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ fontSize: 13 }}>{a.name}</Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>{a.avail}</Text>
                  </div>
                  <Button size="small" type={a.booked ? 'default' : 'primary'}
                    style={a.booked ? {} : { background: a.color, borderColor: a.color, fontSize: 11 }}>
                    {a.booked ? 'View' : 'Book'}
                  </Button>
                </div>
              ))}
            </div>
          </SlideShell>
        ),
      },
    ],
  },

  // ── 7. Property Operations (admin+) ──────────────────────────────────────
  {
    id: 'v-07',
    title: 'Property Operations',
    subtitle: 'Packages, visitors, access keys, and parking management',
    duration: '2:30',
    category: 'Operations & Maintenance',
    roles: ['super_admin_condo', 'admin'],
    icon: <CarOutlined />,
    accentColor: '#0ea5e9',
    slides: [
      {
        heading: 'Property Operations Hub',
        visual: (
          <HeroSlide
            icon={<CarOutlined />}
            title="Property Operations Hub"
            subtitle="Run the physical side of your building efficiently"
            color="#0ea5e9"
            bullets={[
              'Package tracking from receipt to pickup',
              'Visitor authorization and access log',
              'Key fob and access card inventory',
              'Parking stall assignments and violations',
            ]}
          />
        ),
      },
      {
        heading: 'Package Tracking',
        visual: (
          <SlideShell>
            <div style={{ width: '100%', maxWidth: 460 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <StatBox label="Awaiting Pickup" value="6" color="#f97316" bg="#fff7ed" />
                  <StatBox label="Today's Arrivals" value="3" color="#0ea5e9" bg="#f0f9ff" />
                </div>
                <Button size="small" type="primary" style={{ background: '#0ea5e9', borderColor: '#0ea5e9', alignSelf: 'center', fontSize: 11 }}>+ Log Package</Button>
              </div>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                <FakeRow header cells={['Tracking #', 'Carrier', 'Unit', 'Status']} />
                {[
                  { track: '1Z999...', carrier: 'UPS', unit: '202', status: 'NOTIFIED', sc: '#0ea5e9', sb: '#f0f9ff' },
                  { track: '9261...', carrier: 'Canada Post', unit: '305', status: 'RECEIVED', sc: '#f97316', sb: '#fff7ed' },
                  { track: 'TBA7...', carrier: 'Amazon', unit: '101', status: 'PICKED UP', sc: '#22c55e', sb: '#f0fdf4' },
                ].map(r => (
                  <FakeRow key={r.track} cells={[
                    <Text type="secondary" style={{ fontSize: 11 }}>{r.track}</Text>,
                    r.carrier,
                    <Text strong style={{ fontSize: 12 }}>Unit {r.unit}</Text>,
                    <FakeTag color={r.sc} bg={r.sb} label={r.status} />,
                  ]} />
                ))}
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Visitor Management',
        visual: (
          <SlideShell>
            <div style={{ width: '100%', maxWidth: 460 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <Text strong style={{ fontSize: 14 }}>Authorized Visitors</Text>
                <Button size="small" style={{ fontSize: 11 }}>+ Authorize Visitor</Button>
              </div>
              {[
                { name: 'David Thompson', unit: '202', type: 'Family', valid: 'Until Dec 31', status: 'ACTIVE', sc: '#22c55e', sb: '#f0fdf4' },
                { name: 'Handyman Pro', unit: '305', type: 'Contractor', valid: 'Apr 15–22', status: 'ACTIVE', sc: '#22c55e', sb: '#f0fdf4' },
                { name: 'Claire Martin', unit: '101', type: 'Pet Sitter', valid: 'May 3–10', status: 'PENDING', sc: '#f59e0b', sb: '#fffbeb' },
              ].map(v => (
                <div key={v.name} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  border: '1px solid #f0f0f0', borderRadius: 6, marginBottom: 6,
                }}>
                  <Avatar size={32} icon={<UserOutlined />} style={{ background: '#0ea5e9', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ fontSize: 12 }}>{v.name}</Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>Unit {v.unit} · {v.type} · {v.valid}</Text>
                  </div>
                  <FakeTag color={v.sc} bg={v.sb} label={v.status} />
                </div>
              ))}
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Access Key & Fob Inventory',
        visual: (
          <SlideShell>
            <div style={{ width: '100%', maxWidth: 440 }}>
              <Row gutter={8} style={{ marginBottom: 12 }}>
                <Col span={6}><StatBox label="Issued" value="91" color="#0ea5e9" bg="#f0f9ff" /></Col>
                <Col span={6}><StatBox label="Available" value="14" color="#22c55e" bg="#f0fdf4" /></Col>
                <Col span={6}><StatBox label="Lost" value="3" color="#ef4444" bg="#fef2f2" /></Col>
                <Col span={6}><StatBox label="Returned" value="7" color="#8c8c8c" bg="#f5f5f5" /></Col>
              </Row>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                <FakeRow header cells={['Key #', 'Type', 'Unit', 'Status']} />
                {[
                  { num: 'K-1024', type: 'Fob', unit: '101', status: 'ISSUED', sc: '#0ea5e9', sb: '#f0f9ff' },
                  { num: 'K-1031', type: 'Key Card', unit: '—', status: 'AVAILABLE', sc: '#22c55e', sb: '#f0fdf4' },
                  { num: 'K-0988', type: 'Fob', unit: '305', status: 'LOST', sc: '#ef4444', sb: '#fef2f2' },
                ].map(r => (
                  <FakeRow key={r.num} cells={[
                    <Text type="secondary" style={{ fontSize: 11 }}>{r.num}</Text>,
                    r.type,
                    <Text strong style={{ fontSize: 12 }}>{r.unit}</Text>,
                    <FakeTag color={r.sc} bg={r.sb} label={r.status} />,
                  ]} />
                ))}
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Parking Management',
        visual: (
          <SlideShell>
            <div style={{ width: 400 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>Parking Inventory</Text>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 14 }}>
                {[
                  { stall: 'P01', unit: '101', type: 'assigned' },
                  { stall: 'P02', unit: '202', type: 'assigned' },
                  { stall: 'P03', unit: null, type: 'available' },
                  { stall: 'P04', unit: '305', type: 'assigned' },
                  { stall: 'P05', unit: null, type: 'available' },
                  { stall: 'P06', unit: '411', type: 'assigned' },
                  { stall: 'P07', unit: null, type: 'visitor' },
                  { stall: 'P08', unit: null, type: 'visitor' },
                  { stall: 'P09', unit: '502', type: 'assigned' },
                  { stall: 'P10', unit: null, type: 'available' },
                ].map(s => (
                  <div key={s.stall} style={{
                    borderRadius: 4, padding: '6px 4px', textAlign: 'center',
                    background: s.type === 'assigned' ? '#dbeafe' : s.type === 'visitor' ? '#fef3c7' : '#d1fae5',
                    border: `1px solid ${s.type === 'assigned' ? '#93c5fd' : s.type === 'visitor' ? '#fde68a' : '#6ee7b7'}`,
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: s.type === 'assigned' ? '#1d4ed8' : s.type === 'visitor' ? '#92400e' : '#065f46' }}>{s.stall}</div>
                    <div style={{ fontSize: 9, color: '#6b7280' }}>{s.unit ?? s.type}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 2 }} /><Text style={{ fontSize: 11 }}>Assigned</Text></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 2 }} /><Text style={{ fontSize: 11 }}>Available</Text></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 2 }} /><Text style={{ fontSize: 11 }}>Visitor</Text></div>
              </div>
            </div>
          </SlideShell>
        ),
      },
    ],
  },

  // ── 8. Analytics & Reports (admin+, board-member) ─────────────────────────
  {
    id: 'v-08',
    title: 'Analytics & Financial Reports',
    subtitle: 'Budgets, reserve fund, collection analytics, and export tools',
    duration: '2:45',
    category: 'Financial Management',
    roles: ['super_admin_condo', 'admin', 'board-member'],
    icon: <BarChartOutlined />,
    accentColor: '#f59e0b',
    slides: [
      {
        heading: 'Analytics & Reports Overview',
        visual: (
          <HeroSlide
            icon={<BarChartOutlined />}
            title="Analytics & Financial Reports"
            subtitle="Data-driven insights for better property decisions"
            color="#f59e0b"
            bullets={[
              'Collection rate trends and aging analysis',
              'Budget vs. actual expense comparison',
              'Reserve fund balance and projection',
              'Export reports in PDF, Excel, or Word',
            ]}
          />
        ),
      },
      {
        heading: 'Financial Management Hub',
        visual: (
          <SlideShell>
            <div style={{ width: 420 }}>
              {/* Fake tab strip */}
              <div style={{ display: 'flex', borderBottom: '2px solid #f0f0f0', marginBottom: 12, gap: 0 }}>
                {['Expenses', 'Budget', 'Reserve Fund'].map((t, i) => (
                  <div key={t} style={{
                    padding: '5px 12px', fontSize: 12, fontWeight: i === 0 ? 600 : 400, cursor: 'pointer',
                    borderBottom: i === 0 ? '2px solid #f59e0b' : 'none', marginBottom: i === 0 ? -2 : 0,
                    color: i === 0 ? '#f59e0b' : '#8c8c8c',
                  }}>{t}</div>
                ))}
              </div>
              <Row gutter={8} style={{ marginBottom: 12 }}>
                <Col span={8}><StatBox label="Total Expenses" value="$61.2K" color="#f59e0b" bg="#fffbeb" /></Col>
                <Col span={8}><StatBox label="This Month" value="$15.4K" color="#f97316" bg="#fff7ed" /></Col>
                <Col span={8}><StatBox label="vs. Budget" value="-7%" color="#ef4444" bg="#fef2f2" /></Col>
              </Row>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                <FakeRow header cells={['Category', 'Amount', 'Budget', '']} />
                {[
                  { cat: 'Maintenance', amt: '$18,200', bud: '$15,000', over: true },
                  { cat: 'Insurance', amt: '$27,200', bud: '$28,000', over: false },
                  { cat: 'Landscaping', amt: '$9,400', bud: '$12,000', over: false },
                  { cat: 'Utilities', amt: '$6,400', bud: '$6,000', over: true },
                ].map(r => (
                  <FakeRow key={r.cat} cells={[
                    r.cat,
                    <Text strong style={{ fontSize: 12 }}>{r.amt}</Text>,
                    <Text type="secondary" style={{ fontSize: 11 }}>{r.bud}</Text>,
                    <FakeTag color={r.over ? '#ef4444' : '#22c55e'} bg={r.over ? '#fef2f2' : '#f0fdf4'} label={r.over ? 'Over' : 'Under'} />,
                  ]} />
                ))}
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Reserve Fund Tracking',
        visual: (
          <SlideShell>
            <div style={{ width: 400 }}>
              <div style={{ background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', border: '1px solid #c4b5fd', borderRadius: 8, padding: '14px 16px', marginBottom: 14 }}>
                <Text strong style={{ fontSize: 15, color: '#7c3aed', display: 'block', marginBottom: 10 }}>Reserve Fund Summary</Text>
                <Row gutter={[8, 8]}>
                  <Col span={12}><StatBox label="Current Balance" value="$214K" color="#8b5cf6" bg="#fff" /></Col>
                  <Col span={12}><StatBox label="Annual Contribution" value="$48K" color="#10b981" bg="#fff" /></Col>
                  <Col span={12}><StatBox label="YTD Withdrawals" value="$12K" color="#f97316" bg="#fff" /></Col>
                  <Col span={12}><StatBox label="Study Projection" value="$218K" color="#0ea5e9" bg="#fff" /></Col>
                </Row>
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '8px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <CheckCircleOutlined style={{ color: '#22c55e' }} />
                  <Text strong style={{ fontSize: 12, color: '#166534' }}>Fund is on track</Text>
                </div>
                <Text style={{ fontSize: 11, color: '#166534' }}>Balance is within 2% of the Reserve Fund Study target for 2025.</Text>
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Collection Rate Analytics',
        visual: (
          <SlideShell>
            <div style={{ width: 400 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>Collection Rate — Last 6 Months</Text>
              <div style={{ display: 'flex', gap: 8, height: 100, alignItems: 'flex-end', marginBottom: 12 }}>
                {[
                  { month: 'Nov', rate: 91, color: '#f59e0b' },
                  { month: 'Dec', rate: 88, color: '#ef4444' },
                  { month: 'Jan', rate: 93, color: '#f59e0b' },
                  { month: 'Feb', rate: 96, color: '#22c55e' },
                  { month: 'Mar', rate: 95, color: '#22c55e' },
                  { month: 'Apr', rate: 94, color: '#22c55e' },
                ].map(b => (
                  <div key={b.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ fontSize: 10, color: b.color, fontWeight: 700 }}>{b.rate}%</div>
                    <div style={{
                      width: '100%', borderRadius: '4px 4px 0 0', background: b.color,
                      height: `${(b.rate - 80) * 4}px`,
                    }} />
                    <div style={{ fontSize: 10, color: '#8c8c8c' }}>{b.month}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, flex: 1, padding: '8px 10px' }}>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>6-Month Avg</Text>
                  <Text strong style={{ fontSize: 16, color: '#22c55e' }}>92.8%</Text>
                </div>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, flex: 1, padding: '8px 10px' }}>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Overdue Accounts</Text>
                  <Text strong style={{ fontSize: 16, color: '#3b82f6' }}>3 units</Text>
                </div>
              </div>
            </div>
          </SlideShell>
        ),
      },
      {
        heading: 'Exporting Reports',
        visual: (
          <SlideShell bg="#f5f5f5">
            <FakeModal title="Export Report" btnLabel="Export" color="#f59e0b">
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Report Type</div>
                <div style={{ border: '1px solid #f59e0b', borderRadius: 4, padding: '4px 8px', fontSize: 12, color: '#f59e0b', background: '#fffbeb' }}>Financial Summary</div>
              </div>
              <FakeFormRow label="Date Range" value="Jan 1, 2025 — Apr 30, 2025" />
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Export Format</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['PDF', 'Excel', 'Word'].map((f, i) => (
                    <div key={f} style={{
                      flex: 1, padding: '5px 0', borderRadius: 4, fontSize: 12, textAlign: 'center',
                      background: i === 0 ? '#f59e0b' : '#f5f5f5',
                      color: i === 0 ? '#fff' : '#595959',
                      border: `1px solid ${i === 0 ? '#f59e0b' : '#d9d9d9'}`,
                    }}>{f}</div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 14, height: 14, border: '2px solid #f59e0b', borderRadius: 3, background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircleOutlined style={{ color: '#fff', fontSize: 8 }} />
                  </div>
                  <Text style={{ fontSize: 11 }}>Include Charts</Text>
                </div>
                <div style={{ flex: 1, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 14, height: 14, border: '2px solid #f59e0b', borderRadius: 3, background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircleOutlined style={{ color: '#fff', fontSize: 8 }} />
                  </div>
                  <Text style={{ fontSize: 11 }}>Detailed Breakdown</Text>
                </div>
              </div>
            </FakeModal>
          </SlideShell>
        ),
      },
    ],
  },
];
